// Режим отображения: кабинет или вид киоска.
//
// Киоск — это ВИД, а не отдельная система личности (решение T-018). Раньше он был
// вторым субъектом с собственным входом на том же устройстве, и из этого росла дыра:
// оба токена лежали рядом, и достаточно было снять `/kiosk` с адреса, чтобы из-под
// привязанного моноблока открылся полный кабинет менеджера. Вид такой дыры не создаёт
// по построению — переключается отображение, а не то, кем ты вошёл.
//
// Флаг принадлежит УСТРОЙСТВУ, а не человеку. Моноблок на кухне настроен показывать
// киоск, и выход повара в конце смены не должен возвращать его в кабинет: следующий,
// кто подойдёт, увидел бы экран аналитики вместо печати.

import { Preferences } from '@capacitor/preferences';

import { PREFERENCE_KEYS } from './preferenceKeys';

export type ViewMode = 'cabinet' | 'kiosk';

// Копия в памяти: роутер спрашивает режим СИНХРОННО при каждом переходе, а чтение
// из хранилища асинхронно. Без копии первый переход после старта уходил бы в кабинет
// и только потом перерисовывался в киоск — моргание на весь экран при каждом запуске.
let cached: ViewMode = 'cabinet';

/**
 * Отразить режим в DOM: вид и ПЛОТНОСТЬ.
 *
 * CSS обязан знать режим по двум причинам.
 *
 * Первая — раскладка: в виде киоска экран занимает нижняя панель, и всё липкое
 * (кнопки режима печати) должно стоять НАД ней, а в кабинете её нет вовсе. Передать
 * это пропсами нельзя — панель рисует layout, а кнопки живут внутри экрана, между
 * ними три уровня компонентов и ни одного общего.
 *
 * Вторая — плотность. `DESIGN.md` разводит `kiosk` и `manager`: 48px тап в перчатках
 * и крупный текст против плотных таблиц кабинета. Токены обеих были объявлены
 * в ui-kit с самого начала и НЕ ПРИМЕНЯЛИСЬ НИГДЕ — атрибут `data-density` не ставил
 * никто, и вся система жила как декларация. Здесь она наконец включается.
 *
 * Плотность привязана к виду, а не к маршруту: экран печати, открытый менеджером
 * из кабинета, это тот же экран — но за столом, а не в перчатках у плиты.
 *
 * ЭКСПОРТИРУЕТСЯ, потому что сохранённое предпочтение — не всегда действующий вид.
 * Сотрудник живёт в киоске по РОЛИ (Д5, T-019), ничего не переключая, и его
 * предпочтение так и остаётся `cabinet`. Вид, который человек видит, знает только
 * `AppLayout` — он и обязан отражать его в DOM. Пока это делало одно лишь хранилище,
 * у повара стояли `data-view=cabinet` и `data-density=manager`: панель киоска
 * схлопывалась в 17px, тап-цели были 44px вместо 48, а текст 13px вместо 16 —
 * то есть вся плотность кухни не включалась у той единственной роли, ради которой
 * она заведена.
 */
export function reflect(mode: ViewMode): void {
  const root = document.documentElement;
  root.dataset.view = mode;
  root.dataset.density = mode === 'kiosk' ? 'kiosk' : 'manager';
}

/** Прочитать сохранённый режим при старте. Вызывается из bootstrap до первого рендера. */
export async function restoreViewMode(): Promise<ViewMode> {
  const { value } = await Preferences.get({ key: PREFERENCE_KEYS.viewMode });
  cached = value === 'kiosk' ? 'kiosk' : 'cabinet';
  reflect(cached);
  return cached;
}

export function getViewMode(): ViewMode {
  return cached;
}

/**
 * Переключить режим.
 *
 * Пишется на ДИСК, а не только в память: моноблок перезагружают ночью, и режим,
 * живущий до перезапуска, каждое утро встречал бы кухню экраном аналитики.
 * Поэтому же он не в `useUiStore` — тот не персистентный.
 */
export async function setViewMode(mode: ViewMode): Promise<void> {
  cached = mode;
  reflect(mode);
  await Preferences.set({ key: PREFERENCE_KEYS.viewMode, value: mode });
}
